import { Box, BoxProps, Flex, useInterval } from "@chakra-ui/react"
import { nanoid } from "@reduxjs/toolkit"
import { AnimatePresence, motion, PanInfo } from "framer-motion"
import React from "react"
import CarouselArrowNavigation from "./CarouselArrowNavigation"
// import "./Carousel.scss"
import CarouselIndicator from "./CarouselIndicator"
import { ImageCarouselAssetEntityHydrated } from "@jackfruit/common"
import { useCarouselWithIndicator } from "./hooks/useCarouselWithIndicator"
import { useCarouselSizeAssets } from "./hooks/useCarouselSizeAssets"

const variants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
  exiting: { opacity: 0 },
}

const dragThreshold = 100
interface Props extends BoxProps {
  images: ImageCarouselAssetEntityHydrated[]
  height?: number
  delay: number
  indicatorConfig: Record<string, any>
}

const CarouselSlide: React.FC<Props> = props => {
  const { images, indicatorConfig } = props
  const displayableImages = images
  const delay = 0

  const { imageWidth, imageHeight } = useCarouselSizeAssets()

  const {
    firstImageIndex,
    currentIndex,
    onHandleNext,
    onSelectImage,
    setPaginate,
  } = useCarouselWithIndicator(displayableImages.length)

  const { path, alt } =
    displayableImages[currentIndex] ?? displayableImages[firstImageIndex]

  const componentNanoid = nanoid()

  useInterval(() => {
    if (delay > 0) {
      onHandleNext()
    }
  }, delay)

  const handleDragEnd = (_event: MouseEvent | TouchEvent, info: PanInfo) => {
    const offsetX = info.offset.x

    if (offsetX > dragThreshold || (offsetX > 0 && offsetX < dragThreshold)) {
      setPaginate(-1)
    } else if (
      offsetX < -dragThreshold ||
      (offsetX < 0 && offsetX > -dragThreshold)
    ) {
      setPaginate(1)
    } else {
      setPaginate(0)
    }
  }

  return (
    <Flex direction="column" className="s__carousel">
      <Box
        aria-label="carousel-container"
        className="s__carousel-container"
        borderRadius={"0.5rem"}
        sx={{
          marginInlineStart: "0",
          marginInlineEnd: "0",
        }}
        zIndex={10}
        height={{ base: "250px", sm: "400px", xl: "440px" }}
        backgroundColor="gray.50"
        maxHeight={{ base: "250px", sm: "400px", xl: "440px" }}
        display="flex"
        alignItems="center"
        overflow="hidden"
        justifyContent={"center"}
        position="relative"
      >
        <AnimatePresence initial={false}>
          <motion.img
            id={`carousel-image-slide${componentNanoid}-${currentIndex}`}
            key={`carousel-image-slide${componentNanoid}-${currentIndex}`}
            initial="hidden"
            animate="visible"
            exit="exiting"
            src={path}
            alt={alt || "carousel-image"}
            variants={variants}
            width={imageWidth}
            height={imageHeight}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: "center",
              borderRadius: "0.5rem",
            }}
            drag="x"
            onDragEnd={handleDragEnd}
            dragConstraints={{ left: 0, right: 0 }}
            transition={{
              duration: 0,
            }}
            className="fade-animation s__carousel_image"
          />
        </AnimatePresence>
        <CarouselArrowNavigation paginate={setPaginate} />
      </Box>

      <CarouselIndicator
        displayableImages={displayableImages}
        currentIndex={currentIndex}
        selectImage={onSelectImage}
        indicatorConfig={indicatorConfig}
      />
    </Flex>
  )
}

export default CarouselSlide
